import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "../App";
import { LOGOS } from "../images";
import { propertiesI18n } from "../i18n";

const LANGS = ["fr", "de", "en"];

export default function PageHeader({ active }) {
  const { lang, setLang, t } = useLang();
  const p = propertiesI18n[lang];

  const links = [
    { to: "/", label: t.footer.home, testid: "pageheader-nav-home" },
    { to: "/#expertise", label: t.nav.expertise, testid: "pageheader-nav-expertise" },
    { to: "/#approche", label: t.nav.about, testid: "pageheader-nav-about" },
    { to: "/objekte", label: p.navLabel, testid: "pageheader-nav-properties", key: "properties" },
    { to: "/#contact", label: t.nav.contact, testid: "pageheader-nav-contact" },
  ];

  return (
    <header data-testid="page-header" className="border-b border-ink/10 bg-ivory">
      <div className="flex items-center justify-between px-5 py-5 md:px-10 lg:px-14">
        <Link to="/" data-testid="pageheader-logo" aria-label="FLEN Immobilier">
          <img src={LOGOS.horizontal} alt="FLEN Immobilier" className="h-10 w-auto md:h-12" />
        </Link>
        <nav className="hidden items-center gap-9 lg:flex" aria-label="Page">
          {links.map((l) =>
            l.key === "properties" ? (
              <div key={l.to} className="group relative" data-testid="pageheader-properties-wrap">
                <Link
                  data-testid={l.testid}
                  to={l.to}
                  className={`group/btn relative text-[12px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 ${
                    (l.key === active || (l.key === "properties" && active === "rentals")) ? "text-bronze" : "text-ink/70 hover:text-ink"
                  }`}
                >
                  {l.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-bronze transition-[width] duration-500 group-hover/btn:w-full" />
                </Link>
                <div
                  data-testid="pageheader-properties-submenu"
                  className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-5 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100"
                >
                  <div className="flex min-w-44 flex-col border border-ink/10 bg-ivory">
                    <Link
                      data-testid="pageheader-sub-properties-sale"
                      to="/objekte"
                      className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-[0.2em] text-ink/60 transition-colors duration-300 hover:bg-bronze/10 hover:text-ink"
                    >
                      {p.forSale}
                    </Link>
                    <Link
                      data-testid="pageheader-sub-properties-rental"
                      to="/rentals"
                      className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-[0.2em] text-ink/60 transition-colors duration-300 hover:bg-bronze/10 hover:text-ink"
                    >
                      {p.rentalMenu}
                    </Link>
                    <Link
                      data-testid="pageheader-sub-properties-sold"
                      to="/objekte/verkauft"
                      className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-[0.2em] text-ink/60 transition-colors duration-300 hover:bg-bronze/10 hover:text-ink"
                    >
                      {p.soldMenu}
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={l.to}
                data-testid={l.testid}
                to={l.to}
                className={`group relative text-[12px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 ${
                  (l.key === active || (l.key === "properties" && active === "rentals")) ? "text-bronze" : "text-ink/70 hover:text-ink"
                }`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-bronze transition-[width] duration-500 group-hover:w-full" />
              </Link>
            )
          )}
        </nav>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] text-ink/60" data-testid="pageheader-language-switcher">
            {LANGS.map((l, i) => (
              <span key={l} className="flex items-center gap-2">
                {i > 0 && <span className="text-ink/25">|</span>}
                <button
                  data-testid={`pageheader-lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`uppercase transition-colors duration-300 ${lang === l ? "font-semibold text-bronze" : "hover:text-ink"}`}
                >
                  {l}
                </button>
              </span>
            ))}
          </div>
          <Link
            to="/#contact"
            data-testid="pageheader-cta"
            className="hidden items-center gap-3 border border-ink/30 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-ink transition-colors duration-500 hover:border-bronze hover:bg-bronze hover:text-ivory sm:flex"
          >
            {t.nav.cta}
            <ArrowRight size={13} />
          </Link>
        </div>
      </div>
      <nav className="flex gap-6 overflow-x-auto px-5 pb-4 lg:hidden" aria-label="Page mobile">
        {links.map((l) => (
          <Link
            key={l.to}
            data-testid={`mobile-${l.testid}`}
            to={l.to}
            className={`whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.2em] ${
              l.key === active ? "text-bronze" : "text-ink/60"
            }`}
          >
            {l.label}
          </Link>
        ))}
        <Link
          data-testid="mobile-pageheader-nav-rentals"
          to="/rentals"
          className={`whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.2em] ${
            active === "rentals" ? "text-bronze" : "text-ink/60"
          }`}
        >
          {p.rentalMenu}
        </Link>
      </nav>
    </header>
  );
}
